import { useEffect, useState } from 'react'
import { Form, Input, InputNumber, Message, Modal } from '@arco-design/web-react'
import { adjustChannelBalanceApi } from '@/api/channels'
import type { Channel } from '@/types'
import { formatMicroUsd } from '@/utils/quota'

interface Props {
  visible: boolean
  channel: Channel | null
  onCancel: () => void
  onSuccess: () => void
}

export default function ChannelBalanceAdjustModal({ visible, channel, onCancel, onSuccess }: Props) {
  const [form] = Form.useForm<{ targetBalanceUsd: number; reason: string }>()
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!visible || !channel) return
    form.resetFields()
    form.setFieldsValue({
      targetBalanceUsd:
        channel.estimatedBalanceMicroUsd != null
          ? channel.estimatedBalanceMicroUsd / 1_000_000
          : undefined,
      reason: '',
    })
  }, [channel, form, visible])

  const handleOk = async () => {
    if (!channel) return
    try {
      const values = await form.validate()
      setSubmitting(true)
      await adjustChannelBalanceApi(channel.id, {
        target_balance_usd: values.targetBalanceUsd,
        reason: values.reason.trim(),
      })
      Message.success('估算余额已调整')
      onSuccess()
      onCancel()
    } catch (err) {
      if (err instanceof Error && err.message) Message.error(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title={channel ? `余额调整 · ${channel.name}` : '余额调整'}
      visible={visible}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={submitting}
      okText="确认调整"
      cancelText="取消"
      unmountOnExit
    >
      {channel && (
        <div className="mb-3 rounded-lg border border-[#EAECEF] bg-[#F8FAFC] p-3 text-xs text-slate-600">
          当前估算余额：
          <span className="ml-1 font-medium text-slate-900">
            {channel.estimatedBalanceMicroUsd != null
              ? formatMicroUsd(channel.estimatedBalanceMicroUsd)
              : '-'}
          </span>
          <div className="mt-1 text-slate-500">调整将写入审计记录，不会修改上游查询余额。</div>
        </div>
      )}
      <Form form={form} layout="vertical">
        <Form.Item
          field="targetBalanceUsd"
          label="目标估算余额 (USD)"
          rules={[{ required: true, message: '请输入目标余额' }]}
        >
          <InputNumber min={0} precision={6} style={{ width: '100%' }} placeholder="例如 100.5" />
        </Form.Item>
        <Form.Item
          field="reason"
          label="调整原因"
          rules={[{ required: true, message: '请填写调整原因' }]}
        >
          <Input.TextArea rows={3} placeholder="例如：上游充值 $100" />
        </Form.Item>
      </Form>
    </Modal>
  )
}
